/**
 * Re-Authentication Context
 * Handles session expiration gracefully with an in-place login modal
 * instead of hard-redirecting to the login page
 * 
 * This allows users to re-authenticate without losing their work
 * (e.g., unsaved form data, current page state)
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { notifyReAuthCancelled } from '../services/apiClient';

// Re-authentication reasons
export const ReAuthReason = {
  SESSION_EXPIRED: 'session_expired',
  SESSION_REVOKED: 'session_revoked', 
  ACCOUNT_DISABLED: 'account_disabled',
  TOKEN_INVALID: 'token_invalid',
};

const ReAuthContext = createContext(null);

/**
 * Hook to access re-authentication context
 * @returns {Object} ReAuth context value
 */
export const useReAuth = () => {
  const context = useContext(ReAuthContext);
  if (!context) {
    throw new Error('useReAuth must be used within a ReAuthProvider');
  }
  return context;
};

/**
 * Re-Authentication Provider Component
 * Manages modal state and pending request queue
 */
export const ReAuthProvider = ({ children }) => {
  // Modal visibility state
  const [showModal, setShowModal] = useState(false);
  
  // Reason for re-auth (affects messaging in modal)
  const [reason, setReason] = useState(ReAuthReason.SESSION_EXPIRED);
  
  // Pre-filled email from cached user (convenience)
  const [prefilledEmail, setPrefilledEmail] = useState('');
  
  // Track whether re-auth is actively in progress
  const [isReAuthenticating, setIsReAuthenticating] = useState(false);
  
  // Queue of pending request resolvers waiting for re-auth
  // Each entry: { resolve, reject, originalRequest }
  const pendingRequestsRef = useRef([]);
  
  // Timeout for pending requests (5 minutes)
  const REQUEST_TIMEOUT_MS = 5 * 60 * 1000;
  
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Triggered when re-auth is needed (from apiClient)
   * Shows the modal and queues the request
   */
  const triggerReAuth = useCallback((requestInfo = {}) => {
    const { email, reason: reAuthReason, originalRequest, resolve, reject } = requestInfo;
    
    // Set reason and email for modal display
    if (reAuthReason) {
      setReason(reAuthReason);
    }
    if (email) {
      setPrefilledEmail(email);
    }
    
    // Queue the request if resolver provided
    if (resolve && reject) {
      const timeoutId = setTimeout(() => {
        // Remove from queue and reject after timeout
        pendingRequestsRef.current = pendingRequestsRef.current.filter(
          req => req.originalRequest !== originalRequest
        );
        reject(new Error('Re-authentication timeout'));
      }, REQUEST_TIMEOUT_MS);
      
      pendingRequestsRef.current.push({
        resolve,
        reject,
        originalRequest,
        timeoutId,
      });
    }
    
    // Show modal (only once even for multiple concurrent requests)
    if (!showModal) {
      setShowModal(true);
    }
  }, [showModal, REQUEST_TIMEOUT_MS]);

  /**
   * Called on successful re-authentication
   * Resolves all pending requests so they can retry
   */
  const onReAuthSuccess = useCallback((newAccessToken) => {
    setShowModal(false);
    setIsReAuthenticating(false);
    
    // Resolve all pending requests with the new token
    const pending = [...pendingRequestsRef.current];
    pendingRequestsRef.current = [];
    
    pending.forEach(({ resolve, timeoutId }) => {
      clearTimeout(timeoutId);
      resolve(newAccessToken);
    });
  }, []);

  /**
   * Called when user cancels re-authentication
   * Rejects all pending requests and redirects to login
   */
  const onReAuthCancel = useCallback(() => {
    setShowModal(false);
    setIsReAuthenticating(false);
    
    // Reject all pending requests
    const pending = [...pendingRequestsRef.current];
    pendingRequestsRef.current = [];
    
    pending.forEach(({ reject, timeoutId }) => {
      clearTimeout(timeoutId);
      reject(new Error('Re-authentication cancelled'));
    });
    
    // Notify apiClient to reject all queued requests
    notifyReAuthCancelled();
    
    // Dispatch full logout event (clears auth state)
    window.dispatchEvent(new CustomEvent('auth:logout'));
    
    // Navigate to login, preserving the intended destination
    navigate('/login', { 
      replace: true, 
      state: { from: location } 
    });
  }, [navigate, location]);

  /**
   * Called when account is disabled (cannot re-auth)
   * Forces redirect to login with message
   */
  const onAccountDisabled = useCallback(() => {
    setShowModal(false);
    setIsReAuthenticating(false);
    
    // Reject all pending requests
    const pending = [...pendingRequestsRef.current];
    pendingRequestsRef.current = [];
    
    pending.forEach(({ reject, timeoutId }) => {
      clearTimeout(timeoutId);
      reject(new Error('Account disabled'));
    });
    
    // Notify apiClient to reject all queued requests
    notifyReAuthCancelled();
    
    // Dispatch full logout event
    window.dispatchEvent(new CustomEvent('auth:logout'));
    
    // Navigate to login
    navigate('/login', { replace: true });
  }, [navigate]);

  // Listen for re-auth needed events from apiClient
  useEffect(() => {
    const handleReAuthNeeded = (event) => {
      const { email, reason: reAuthReason, originalRequest, resolve, reject } = event.detail || {};
      triggerReAuth({ email, reason: reAuthReason, originalRequest, resolve, reject });
    };
    
    window.addEventListener('auth:reauth-needed', handleReAuthNeeded);
    
    return () => {
      window.removeEventListener('auth:reauth-needed', handleReAuthNeeded);
      // Clear any pending timeouts on unmount
      pendingRequestsRef.current.forEach(({ timeoutId }) => clearTimeout(timeoutId));
    };
  }, [triggerReAuth]);

  const value = {
    // State
    showModal,
    reason,
    prefilledEmail,
    isReAuthenticating,
    
    // Methods
    triggerReAuth,
    onReAuthSuccess,
    onReAuthCancel,
    onAccountDisabled,
    setIsReAuthenticating,
    
    // Constants
    ReAuthReason,
  };

  return (
    <ReAuthContext.Provider value={value}>
      {children}
    </ReAuthContext.Provider>
  );
};

export default ReAuthContext;
